'use client';

/**
 * Branded Admin Login Page
 * Organisation-specific admin login with SSO provider buttons, email/password form,
 * MFA verification, and captcha — all styled with the organisation's brand colours.
 */

import { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams, useParams } from 'next/navigation';
import { useAuth } from '@/lib/auth/AuthContext';
import { isBannedAuthError } from '@/lib/auth/authErrors';
import { fetchAgentDeactivationMessage } from '@/lib/auth/deactivationMessage';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Shield, Loader2, KeyRound, Building2, LogIn, LogOut, UserCircle2, ShieldAlert } from 'lucide-react';
import Link from 'next/link';
import { buildBrandStyle } from '@/lib/ui/brand';
import { useFavicon } from '@/lib/ui/useFavicon';
import TurnstileWidget from '@/components/auth/TurnstileWidget';
import { MarkdownText } from '@/components/ui/markdown-text';
import { isSafeRedirectUrl } from '@/lib/security/safeRedirect';
import type { OrgSSOProvider, SSOProviderType } from '@/types/organization';
import { SSO_PROVIDER_LABELS } from '@/types/organization';
import { writeBrandedLoginSlugToDocument } from '@/lib/auth/brandedLoginCookie';

// Animated background using org brand CSS custom properties (matches app/r/[...slug]/page.tsx)
function AnimatedBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute -top-1/2 -left-1/4 h-[800px] w-[800px] rounded-full blur-[120px]" style={{ background: 'radial-gradient(circle, rgb(var(--org-primary-rgb) / 0.2) 0%, rgb(var(--org-primary-rgb) / 0.15) 40%, transparent 70%)', animation: 'blob-drift-1 20s ease-in-out infinite', willChange: 'transform' }} />
      <div className="absolute -bottom-1/2 -right-1/4 h-[600px] w-[600px] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgb(var(--org-secondary-rgb) / 0.2) 0%, rgb(var(--org-secondary-rgb) / 0.1) 40%, transparent 70%)', animation: 'blob-drift-2 26s ease-in-out infinite', willChange: 'transform' }} />
      <div className="absolute top-1/3 right-1/4 h-[400px] w-[400px] rounded-full blur-[80px]" style={{ background: 'radial-gradient(circle, rgb(var(--org-primary-rgb) / 0.1) 0%, transparent 70%)', animation: 'blob-drift-3 23s ease-in-out infinite', willChange: 'transform' }} />
    </div>
  );
}

// Branded hero rendered on the left side of the page on `lg+` screens
// (stacks above the login card on mobile). Shows the org logo and the
// optional markdown landing blurb owned by the resolved parent org
// (`team_login_landing_content`). The org name is no longer rendered as
// a fixed title — the heading is authored via the landing content so it
// can be edited or removed (GitLab #714).
//
// The copy widens to `max-w-2xl` on `lg+` so it uses the space between
// the hero and the sign-in card rather than wrapping into a narrow,
// squashed column (GitLab #796).
function BrandedLandingHero({
  org,
  orgDisplayName,
  fallbackTagline,
}: {
  org: OrgBranding | null;
  orgDisplayName: string;
  fallbackTagline?: string;
}) {
  const landingContent = org?.team_login_landing_content;
  return (
    <div className="flex-1 text-center lg:text-left space-y-6">
      {org?.brand_logo_url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={org.brand_logo_url}
          alt={orgDisplayName}
          className="h-16 mx-auto lg:mx-0 drop-shadow-lg object-contain max-w-[260px]"
        />
      ) : (
        <div className="h-16 w-16 rounded-full glass-strong flex items-center justify-center mx-auto lg:mx-0 shadow-lg">
          <Building2 className="h-8 w-8" style={{ color: 'var(--org-primary)' }} />
        </div>
      )}
      {landingContent && landingContent.trim() !== '' ? (
        <div
          className="max-w-md lg:max-w-2xl mx-auto lg:mx-0"
          data-testid="team-login-landing-content"
        >
          <MarkdownText
            content={landingContent}
            variant="large"
            className="text-muted-foreground"
          />
        </div>
      ) : fallbackTagline ? (
        <p className="text-lg text-muted-foreground max-w-md lg:max-w-2xl mx-auto lg:mx-0">
          {fallbackTagline}
        </p>
      ) : null}
    </div>
  );
}

// SSO provider icon mapping
function SSOProviderIcon({ providerType }: { providerType: SSOProviderType }) {
  switch (providerType) {
    case 'azure_ad':
      return (<svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M5.483 21.3H24L14.025 4.013l-3.038 8.347 5.836 6.938L5.483 21.3zM13.23 2.7L6.105 8.677 0 19.253h5.505l7.725-16.553z" /></svg>);
    case 'google_workspace':
      return (<svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>);
    default:
      return <LogIn className="h-5 w-5" />;
  }
}

// Organisation branding type (fetched from public query)
interface OrgBranding {
  id: string;
  name: string;
  slug: string;
  parent_org_id: string | null;
  brand_name?: string;
  brand_logo_url?: string;
  background_image_url?: string;
  favicon_url?: string;
  primary_color: string;
  secondary_color: string;
  // B-MR-13 — markdown blurb owned by the resolved parent org and
  // rendered above the login card. Optional; many parents will not
  // configure one.
  team_login_landing_content?: string | null;
}

// Defensive cap on the parent-walk depth — mirrors `MAX_PARENT_WALK_DEPTH`
// in `lib/auth/login-url.ts`.
const MAX_PARENT_WALK_DEPTH = 8;

type LoginStep = 'credentials' | 'mfa';

const MFA_NO_VERIFIED_FACTOR_MESSAGE =
  'Two-factor authentication is required for your account, but no verified authenticator app was found. Please contact your administrator to set up two-factor authentication.';

// ---------------------------------------------------------------------------
// Main content component
// ---------------------------------------------------------------------------
function BrandedAdminLoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = useParams();
  const orgSlug = params['org-slug'] as string;
  const { signIn, verifyMFA, getMFAFactors, getAAL, user, loading: authLoading } = useAuth();

  // Organisation & SSO state
  const [org, setOrg] = useState<OrgBranding | null>(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgNotFound, setOrgNotFound] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<OrgSSOProvider[]>([]);

  // Form state
  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [error, setError] = useState('');
  // Configured Team-member deactivation message, shown inline when a
  // deactivated (banned) agent attempts to sign in.
  const [deactivatedMessage, setDeactivatedMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [ssoLoading, setSsoLoading] = useState<string | null>(null);
  // When an existing session is present we surface a "you're already
  // signed in" prompt instead of silently redirecting to /admin. This
  // mirrors the branded customer login and helps staff who land here
  // mid-session (eg from a bookmark) see whose account is active and
  // switch in one click. MFA-pending sessions are NOT routed through
  // this prompt — they fall through to the existing MFA step transition
  // so the challenge can be completed.
  const [signedInUser, setSignedInUser] = useState<{
    email: string;
    isAdmin: boolean;
  } | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const brandStyle = buildBrandStyle(org?.primary_color || '#0f766e', org?.secondary_color || '#f97316');
  const orgDisplayName = org?.brand_name || org?.name || orgSlug;

  // Show the resolved (root parent) org's custom favicon in the browser tab (#551).
  useFavicon(org?.favicon_url);

  // Fetch organisation branding by slug (public query).
  //
  // Branded admin login is single-tenanted at the root parent level. If the
  // requested slug belongs to a child organisation we walk `parent_org_id`
  // to the top of the chain and `router.replace` to the parent's branded
  // URL. Staff of child orgs sign in via their parent's login page.
  useEffect(() => {
    const fetchOrg = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error: fetchError } = await (supabase as any)
          .from('organizations')
          .select('id, name, slug, parent_org_id, brand_name, brand_logo_url, background_image_url, favicon_url, primary_color, secondary_color, team_login_landing_content')
          .eq('slug', orgSlug)
          .eq('status', 'active')
          .single();
        if (fetchError || !data) {
          setOrgNotFound(true);
          return;
        }

        // Walk up to the root parent if this is a child organisation.
        let current = data as OrgBranding;
        for (let depth = 0; depth < MAX_PARENT_WALK_DEPTH && current.parent_org_id; depth += 1) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: parent, error: parentError } = await (supabase as any)
            .from('organizations')
            .select('id, name, slug, parent_org_id, brand_name, brand_logo_url, background_image_url, favicon_url, primary_color, secondary_color, team_login_landing_content')
            .eq('id', current.parent_org_id)
            .eq('status', 'active')
            .single();
          if (parentError || !parent) {
            // Parent disabled or missing — treat as not found rather than
            // surfacing a half-resolved branded page.
            setOrgNotFound(true);
            return;
          }
          current = parent as OrgBranding;
        }

        if (current.slug !== orgSlug) {
          // We resolved to a different (root) org — redirect there and keep
          // the loading spinner up so the branded UI does not flash.
          // Preserve any query string (e.g. `?error=…`, `?redirect=…`).
          const qs = searchParams.toString();
          router.replace(`/admin/login/${current.slug}${qs ? `?${qs}` : ''}`);
          return;
        }

        setOrg(current);
        setOrgLoading(false);
      } catch {
        setOrgNotFound(true);
        setOrgLoading(false);
      }
    };
    fetchOrg();
  }, [orgSlug, router, searchParams]);

  // Fetch SSO providers for this organisation (graceful if API not yet available).
  //
  // We scope by the resolved root org's `id` (not the URL slug) because the
  // providers API filters on `organization_id`. By this point `org` has been
  // walked up to the root parent, so `org.id` is always the parent org's id
  // — which is where corporate SSO is configured. Passing `org_slug` here
  // would be silently ignored by the API and would leak every other org's
  // enabled providers onto this branded login page.
  useEffect(() => {
    if (!org?.id) return;
    const fetchSSO = async () => {
      try {
        const res = await fetch(`/api/auth/sso/providers?org_id=${encodeURIComponent(org.id)}`);
        if (res.ok) {
          const data = await res.json();
          setSsoProviders(data.providers ?? []);
        }
      } catch {
        console.warn('[SSO] Could not fetch SSO providers — API may not be deployed yet');
      }
    };
    fetchSSO();
  }, [org?.id]);

  // Check for OAuth error in URL params
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) setError(decodeURIComponent(errorParam));
  }, [searchParams]);

  // Detect an existing session. MFA-pending sessions still drop into
  // the MFA step automatically (so the challenge can be completed); any
  // other authenticated state surfaces an "already signed in" prompt
  // instead of silently bouncing to `/admin`. The customer/admin
  // distinction follows the same `user_role_assignments` rule used by
  // the middleware — empty = customer, populated = admin.
  useEffect(() => {
    if (authLoading || !user) return;
    if (step === 'mfa') return;
    let cancelled = false;
    (async () => {
      try {
        const { currentLevel, nextLevel } = await getAAL();
        if (currentLevel === 'aal1' && nextLevel === 'aal2') {
          const { factors } = await getMFAFactors();
          const verified = factors.filter((f) => f.status === 'verified');
          if (verified.length > 0) {
            if (!cancelled) {
              setFactorId(verified[0].id);
              setStep('mfa');
            }
            return;
          }
          // aal2 expected but no verified factor — fall through to the
          // "already signed in" prompt so the operator can sign out and
          // recover, rather than dumping them straight at /admin where
          // the same condition would loop.
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: assignment } = await (supabase as any)
          .from('user_role_assignments')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        setSignedInUser({
          email: user.email ?? '',
          isAdmin: !!assignment,
        });
      } catch (err) {
        console.error('Error detecting existing session:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading, step, getAAL, getMFAFactors]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      setSignedInUser(null);
    } catch (err) {
      console.error('Error signing out:', err);
    } finally {
      setSigningOut(false);
    }
  };

  const handleContinueAsCurrentUser = () => {
    if (!signedInUser) return;
    // Customers can't access /admin (middleware blocks them), so hand
    // them off to the customer dashboard instead. Admins go to /admin.
    router.push(signedInUser.isAdmin ? '/admin' : '/customer/dashboard');
  };

  // --- Handlers ---
  const handleSSOLogin = async (provider: OrgSSOProvider) => {
    setError(''); setSsoLoading(provider.id);
    try {
      const res = await fetch('/api/auth/sso/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider_id: provider.id }),
      });
      if (res.ok) {
        const data = await res.json();
        // `/api/auth/sso/initiate` returns `authorisation_url`. The IdP
        // host is provider-specific (per-org config), so we cannot use a
        // static allow-list — instead we require a well-formed https URL
        // with no credentials/control chars to block javascript: et al.
        const target: unknown = data.authorisation_url ?? data.redirect_url;
        if (typeof target === 'string') {
          let parsed: URL | null = null;
          try { parsed = new URL(target); } catch { parsed = null; }
          if (
            parsed
            && parsed.protocol === 'https:'
            && parsed.username === ''
            && parsed.password === ''
            && isSafeRedirectUrl(target, { allowedHosts: [parsed.hostname] })
          ) {
            window.location.href = target;
            return;
          }
          console.warn('[admin-login] Refused to redirect to untrusted SSO URL');
        }
      }
      // Surface the real reason instead of the generic "not yet available"
      // string. The server already returns admin-safe messages keyed by
      // `code` (sso_misconfigured / sso_idp_unreachable / sso_internal_error
      // — see /api/auth/sso/initiate). Fall back to the generic copy if the
      // body is unreadable so we never break the page on a malformed
      // response.
      let serverMessage: string | null = null;
      try {
        const body = await res.json();
        if (body && typeof body.error === 'string') serverMessage = body.error;
      } catch { /* ignore */ }
      setError(
        serverMessage
        ?? 'SSO sign-in is not yet available. Please use email and password.',
      );
      setSsoLoading(null);
    } catch {
      setError('An unexpected error occurred whilst initiating SSO.');
      setSsoLoading(null);
    }
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setDeactivatedMessage(null); setLoading(true);
    // Set once a client-side navigation is under way. The button must stay
    // busy until Next has actually moved off this page, otherwise it snaps
    // back to "Sign in" for the duration of the redirect.
    let navigating = false;
    try {
      const { error, mfaRequired } = await signIn(email, password, captchaToken ?? undefined, org?.id);
      if (error) {
        // Deactivated agents are banned in GoTrue — show the configured
        // Team-member deactivation message inline instead of the raw
        // "User is banned" error.
        if (isBannedAuthError(error)) {
          setDeactivatedMessage(await fetchAgentDeactivationMessage());
          setCaptchaToken(null); setTurnstileResetKey(k => k + 1);
          setLoading(false); return;
        }
        setError(error.message); setCaptchaToken(null); setTurnstileResetKey(k => k + 1);
        setLoading(false); return;
      }
      if (mfaRequired) {
        const { factors } = await getMFAFactors();
        const verified = factors.filter(f => f.status === 'verified');
        if (verified.length > 0) { setFactorId(verified[0].id); setStep('mfa'); }
        else {
          // No verified MFA factor — never grant access without the second
          // factor (issue #285).
          console.warn('[MFA] MFA required but no verified factors found');
          await supabase.auth.signOut();
          setSignedInUser(null);
          setError(MFA_NO_VERIFIED_FACTOR_MESSAGE);
          setCaptchaToken(null); setTurnstileResetKey(k => k + 1);
        }
      } else {
        // Persist the branded slug so the next logout returns the user to
        // this tenant's `/admin/login/{slug}` page (issue #706 follow-up).
        writeBrandedLoginSlugToDocument(orgSlug);
        navigating = true;
        router.push('/admin');
      }
    } catch {
      setError('An unexpected error occurred');
      setCaptchaToken(null); setTurnstileResetKey(k => k + 1);
      navigating = false;
    } finally {
      if (!navigating) setLoading(false);
    }
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault(); if (!factorId) return;
    setError(''); setLoading(true);
    // See `handleCredentialsSubmit` — keeps the button busy across the
    // post-verification redirect.
    let navigating = false;
    try {
      const { error } = await verifyMFA(factorId, mfaCode);
      if (error) {
        const code = (error as { code?: string }).code;
        let msg = error.message || 'Invalid verification code';
        if (code === 'mfa_verification_failed') msg = 'Invalid code. Please check your authenticator app and try again.';
        else if (code === 'mfa_challenge_expired') msg = 'Verification timed out. Please try again.';
        setError(msg); setMfaCode('');
      } else {
        // MFA passed — full aal2 session is now live. Persist the branded
        // slug so logout returns the user to this tenant's branded page.
        writeBrandedLoginSlugToDocument(orgSlug);
        navigating = true;
        router.push('/admin');
      }
    } catch { setError('An unexpected error occurred'); navigating = false; }
    finally { if (!navigating) setLoading(false); }
  };

  // --- Loading ---
  if (orgLoading || authLoading) {
    return (
      <div className="admin-shell min-h-screen flex items-center justify-center" style={brandStyle}>
        <AnimatedBackground />
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // --- Org not found ---
  if (orgNotFound) {
    return (
      <div className="admin-shell min-h-screen flex items-center justify-center px-4" style={brandStyle}>
        <AnimatedBackground />
        <div className="fixed top-4 right-4 z-50"><ThemeToggle /></div>
        <GlassCard className="w-full max-w-md" variant="strong">
          <GlassCardHeader className="space-y-1 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-2">
              <Building2 className="h-6 w-6 text-amber-500" />
            </div>
            <GlassCardTitle className="text-2xl font-bold">Organisation Not Found</GlassCardTitle>
            <GlassCardDescription>The organisation &quot;{orgSlug}&quot; could not be found.</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">Please check the link you were provided or contact your administrator.</p>
            <Link href="/admin/login"><Button variant="outline" className="w-full">Go to default sign in</Button></Link>
          </GlassCardContent>
        </GlassCard>
      </div>
    );
  }

  // --- Already signed in ---
  //
  // Surfaced when a session is detected and no MFA challenge is
  // pending. The shell matches the main render so brand colours and the
  // org background image stay consistent across states.
  if (signedInUser) {
    return (
      <div
        className={`min-h-screen${!org?.background_image_url ? ' admin-shell' : ''}`}
        style={{
          ...brandStyle,
          ...(org?.background_image_url ? { backgroundImage: `url(${org.background_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' } : {}),
        }}
      >
        {!org?.background_image_url && <AnimatedBackground />}
        <div className="fixed top-4 right-4 z-50"><ThemeToggle /></div>
        <div className="min-h-screen flex items-center justify-center px-6 py-12">
          <div className="container mx-auto">
            <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
              <BrandedLandingHero
                org={org}
                orgDisplayName={orgDisplayName}
                fallbackTagline="Staff sign-in portal"
              />
              <div className="flex-1 w-full max-w-md mx-auto">
                <GlassCard className="w-full" variant="strong">
                  <GlassCardHeader className="space-y-1 text-center">
                    <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                      <UserCircle2 className="h-6 w-6 text-primary" />
                    </div>
                    <GlassCardTitle className="text-2xl font-bold">You&apos;re already signed in</GlassCardTitle>
                    <GlassCardDescription>
                      Signed in as <span className="font-medium">{signedInUser.email}</span>
                      {!signedInUser.isAdmin && <> &middot; customer account</>}
                    </GlassCardDescription>
                  </GlassCardHeader>
                  <GlassCardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground text-center">
                      {signedInUser.isAdmin
                        ? `Continue to the admin panel, or sign out to sign in as a different ${orgDisplayName} staff member.`
                        : `You're signed in as a customer. Sign out to sign in as a ${orgDisplayName} staff member.`}
                    </p>
                    <div className="flex flex-col gap-3">
                      <Button type="button" onClick={handleContinueAsCurrentUser} className="w-full" disabled={signingOut}>
                        {signedInUser.isAdmin ? 'Go to Admin Panel' : 'Continue to Dashboard'}
                      </Button>
                      <Button type="button" variant="outline" onClick={handleSignOut} className="w-full glass" disabled={signingOut}>
                        {signingOut ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing out...</>
                        ) : (
                          <><LogOut className="mr-2 h-4 w-4" /> Sign out &amp; use a different account</>
                        )}
                      </Button>
                    </div>
                  </GlassCardContent>
                </GlassCard>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Main render ---
  const enabledSSOProviders = ssoProviders.filter(p => p.enabled);

  return (
    <div
      className={`min-h-screen${!org?.background_image_url ? ' admin-shell' : ''}`}
      style={{
        ...brandStyle,
        ...(org?.background_image_url ? { backgroundImage: `url(${org.background_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' } : {}),
      }}
    >
      {!org?.background_image_url && <AnimatedBackground />}
      <div className="fixed top-4 right-4 z-50"><ThemeToggle /></div>

      <div className="min-h-screen flex items-center justify-center px-6 py-12">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
            <BrandedLandingHero
              org={org}
              orgDisplayName={orgDisplayName}
              fallbackTagline="Staff sign-in portal"
            />
            <div className="flex-1 w-full max-w-md mx-auto">
              <GlassCard className="w-full" variant="strong">
        <GlassCardHeader className="space-y-1 text-center">
          {/* Brand logo lives in the left hero column; the card uses a
              neutral icon so the logo isn't duplicated on the page. */}
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <GlassCardTitle className="text-2xl font-bold">
            {step === 'credentials' ? `Sign in to ${orgDisplayName}` : 'Two-Factor Authentication'}
          </GlassCardTitle>
          <GlassCardDescription>
            {step === 'credentials' ? 'Sign in with your admin credentials' : 'Enter the code from your authenticator app'}
          </GlassCardDescription>
        </GlassCardHeader>

        <GlassCardContent>
          {step === 'credentials' ? (
            <>
              {/* SSO provider buttons at top */}
              {enabledSSOProviders.length > 0 && (
                <div className="space-y-3 mb-6">
                  {enabledSSOProviders.map((provider) => (
                    <Button
                      key={provider.id}
                      type="button"
                      variant="outline"
                      className="w-full flex items-center justify-center gap-3"
                      onClick={() => handleSSOLogin(provider)}
                      disabled={loading || ssoLoading !== null}
                    >
                      {ssoLoading === provider.id ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <SSOProviderIcon providerType={provider.provider_type} />
                      )}
                      <span>Sign in with {provider.display_name || SSO_PROVIDER_LABELS[provider.provider_type]}</span>
                    </Button>
                  ))}

                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background/80 backdrop-blur-sm px-2 text-muted-foreground">Or continue with email</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Email/password form */}
              <form onSubmit={handleCredentialsSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} autoComplete="email" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link href={`/admin/forgot-password/${orgSlug}`} className="text-sm text-primary hover:underline">Forgot password?</Link>
                  </div>
                  <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={loading} autoComplete="current-password" />
                </div>
                <TurnstileWidget onSuccess={(token) => setCaptchaToken(token)} onError={() => setCaptchaToken(null)} onExpire={() => setCaptchaToken(null)} resetKey={turnstileResetKey} />
                {deactivatedMessage && (
                  <div role="alert" className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 p-3 rounded-md">
                    <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{deactivatedMessage}</span>
                  </div>
                )}
                {error && !deactivatedMessage && <div role="alert" className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">{error}</div>}
                <Button type="submit" className="w-full" disabled={loading || ssoLoading !== null || !captchaToken}>
                  {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</>) : 'Sign In'}
                </Button>
              </form>
            </>
          ) : (
            /* MFA form */
            <form onSubmit={handleMFASubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mfaCode">Verification Code</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="mfaCode" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="000000" value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} required disabled={loading} autoComplete="one-time-code" className="pl-10 text-center text-2xl tracking-widest font-mono" />
                </div>
                <p className="text-xs text-muted-foreground text-center">Enter the 6-digit code from your authenticator app</p>
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">{error}</div>}
              <Button type="submit" className="w-full" disabled={loading || mfaCode.length !== 6}>
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</>) : 'Verify'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => { setStep('credentials'); setMfaCode(''); setError(''); }}>
                Use different account
              </Button>
            </form>
          )}
        </GlassCardContent>
              </GlassCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page export with Suspense boundary
// ---------------------------------------------------------------------------
export default function BrandedAdminLoginPage() {
  const brandStyle = buildBrandStyle('#0f766e', '#f97316');
  return (
    <Suspense fallback={<div className="admin-shell min-h-screen flex items-center justify-center" style={brandStyle}><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
      <BrandedAdminLoginContent />
    </Suspense>
  );
}
